import { Component, Input, OnChanges, OnInit, Output, EventEmitter, SimpleChanges } from "@angular/core";
import { OrendaChemicalHistoryModel, DosageRecommendationResponse } from "@models";
import { OrendaService, GlobalsService } from "@services";

@Component({
    selector: "orenda-chemical-history",
    templateUrl: "./orenda-chemical-history.component.html"
})

export class OrendaChemicalHistoryComponent implements OnChanges
{
    @Input() jobId!: number;
    @Input() customerId!: number;
    @Input() structureId!: number;
    @Output() historyLoaded = new EventEmitter<void>();
 

    orendaChemicalHistories: OrendaChemicalHistoryModel[] = [];
    filteredHistories: OrendaChemicalHistoryModel[] = [];
    CanDeleteLogs: boolean = false;
    

    constructor(private orendaService: OrendaService, private globalService: GlobalsService) {
        this.CanDeleteLogs = GlobalsService.checkPermission("Orenda", "DeleteLogs");
    }

    ngOnChanges(changes: SimpleChanges): void {
        // No jobId check: the Assets tab also runs from the customer page, where there is no job and
        // the history is still the pool's.
        if (this.customerId && this.structureId) {
            this.loadChemicalHistory();
        }
    }

    public async loadChemicalHistory(): Promise<void> {
        try {
            this.orendaChemicalHistories =
                await this.orendaService.getOrendaChemicalHistory(
                    this.customerId
                );

            this.populateFilteredHistories();

            this.historyLoaded.emit();
        } catch (err) {
            console.error(err);
        }
    }

    private populateFilteredHistories(): void {
        if (this.structureId != null && this.orendaChemicalHistories) {
            this.filteredHistories = this.orendaChemicalHistories.filter(
                h => h.structureId === this.structureId
            );
        } else {
            this.filteredHistories = [];
        }
    }

    public getFirstChemicalHistoryItem(selectedStructuerId: number): OrendaChemicalHistoryModel | null {
        return this.orendaChemicalHistories.find(h => h.structureId === selectedStructuerId) || null;
    }

    getSummaryActionKeys(item: OrendaChemicalHistoryModel): string[] {
        const parsed = this.parseActionAndMaintenance(item);
        return parsed?.actions ? Object.keys(parsed.actions) : [];
    }

    getSummaryMaintenanceKeys(item: OrendaChemicalHistoryModel): string[] {
        const parsed = this.parseActionAndMaintenance(item);
        return parsed?.maintenance ? Object.keys(parsed.maintenance) : [];
    }

    getSummaryActions(item: OrendaChemicalHistoryModel, key: string): string {
        const parsed = this.parseActionAndMaintenance(item);
        return parsed?.actions[key]
            ?.map(a => {
                if (a.value) {
                    const rawQty = a.value.quantity;
				    const num = typeof rawQty === 'number' ? rawQty : parseFloat(rawQty);
                    const qty = (!isNaN(num) && isFinite(num)) ? num.toFixed(2) : a.value.quantity;
                    return `${a.name} (${qty} ${a.value.measurement})`;
                }
                return a.name;
            })
            .join(', ') || '';
    }

    getSummaryMaintenance(item: OrendaChemicalHistoryModel, key: string): string {
        const entry = this.parseActionAndMaintenance(item)?.maintenance[key];
        if (!entry) return '';

        const format = (val: any) => {
            const rawQty = val?.quantity;
            const num = typeof rawQty === 'number' ? rawQty : parseFloat(rawQty);
            const qty = (!isNaN(num) && isFinite(num)) ? num.toFixed(2) : rawQty;
            return `${qty} ${val?.measurement}`;
        };

        const parts: string[] = [];
        if (entry.purge) parts.push(`Purge (${format(entry.purge)})`);
        if (entry.weekly) parts.push(`Weekly (${format(entry.weekly)})`);
        if (entry.residentialWeekly) parts.push(`Residential (${format(entry.residentialWeekly)})`);
        if (entry.commercialWeekly) parts.push(`Commercial (${format(entry.commercialWeekly)})`);
        if (entry.useAsNeeded) parts.push(`As Needed (${format(entry.useAsNeeded)})`);

        const category = this.getProductCategory(key);
        const label = category ? `(${category})` : key;

        return `${label}: ${parts.join(', ')}`;
    }

    getProductCategory(name: string): string {
        if (!name) return '';

        const normalized = name.toLowerCase();

        const map: Record<string, string> = {
            'pr-10': 'Phosphate Remover',
            'sc-1000': 'Scale and Metal Control',
            'cv-600': 'Enzymes',
            'cv-700': 'Enzymes + Phosphate Remover',
            'ce-clarifier': 'Chitosan Clarifier + Enzymes',
            'spa-500': 'Pre-Drain Spa Scrub',
            'ce-spa': 'Chitosan Clarifier + Enzymes'
        };

        for (const key in map) {
            if (normalized.includes(key)) return map[key];
        }

        return '';
    }

    private parseActionAndMaintenance(item: OrendaChemicalHistoryModel): DosageRecommendationResponse | null {
        try {
            return JSON.parse(item.actionAndMaintenance);
        } catch {
            return null;
        }
    }

    async deleteHistory(item: OrendaChemicalHistoryModel): Promise<void> {
        await this.orendaService.deleteOrendaChemicalHistory(item.id);
        await this.loadChemicalHistory();
    }
}